// ============================================================================
// SITES API - Frontend
// API client para gestión de centros médicos
// ============================================================================

import { api } from './client';

// ============================================================================
// TYPES
// ============================================================================

export interface Site {
  id: string;
  code: string;
  name: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  logoUrl: string | null;
  agreementPercent: number;
  defaultSocietyId: string | null;
  defaultSocietyName: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  specialtiesCount: number;
  subspecialtiesCount: number;
  specialties?: { id: string; code: string; name: string }[];
  subspecialties?: { id: string; code: string; name: string }[];
}

export interface SiteSelect {
  id: string;
  code: string;
  name: string;
}

export interface CreateSiteDto {
  code: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  logoUrl?: string;
  agreementPercent?: number;
  defaultSocietyId?: string;
}

export interface UpdateSiteDto {
  code?: string;
  name?: string;
  address?: string;
  phone?: string;
  email?: string;
  logoUrl?: string;
  agreementPercent?: number;
  defaultSocietyId?: string;
}

export interface SiteQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  includeInactive?: boolean;
  sortBy?: 'name' | 'code' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export interface SiteLinkedSpecialty {
  id: string;
  code: string;
  name: string;
  isLinked: boolean;
  subspecialties: {
    id: string;
    code: string;
    name: string;
    isLinked: boolean;
  }[];
}

export interface UpdateSiteSpecialtiesDto {
  specialtyIds: string[];
  subspecialtyIds: string[];
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

export const sitesApi = {
  /**
   * Crear un nuevo centro
   */
  create: async (data: CreateSiteDto): Promise<Site> => {
    return api.post<Site>('/sites', data);
  },

  /**
   * Obtener todos los centros (paginado)
   */
  getAll: async (params?: SiteQueryParams): Promise<PaginatedResponse<Site>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.includeInactive) searchParams.append('includeInactive', 'true');
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    
    const query = searchParams.toString();
    return api.get<PaginatedResponse<Site>>(`/sites${query ? `?${query}` : ''}`);
  },

  /**
   * Obtener centros para dropdown
   */
  getForSelect: async (): Promise<SiteSelect[]> => {
    return api.get<SiteSelect[]>('/sites/select');
  },

  /**
   * Obtener un centro por ID
   */
  getById: async (id: string): Promise<Site> => {
    return api.get<Site>(`/sites/${id}`);
  },

  /**
   * Actualizar un centro
   */
  update: async (id: string, data: UpdateSiteDto): Promise<Site> => {
    return api.patch<Site>(`/sites/${id}`, data);
  },

  /**
   * Desactivar un centro
   */
  deactivate: async (id: string): Promise<void> => {
    await api.delete(`/sites/${id}`);
  },

  /**
   * Reactivar un centro
   */
  reactivate: async (id: string): Promise<void> => {
    await api.patch(`/sites/${id}/reactivate`);
  },

  /**
   * Obtener especialidades vinculadas a un centro
   */
  getLinkedSpecialties: async (id: string): Promise<SiteLinkedSpecialty[]> => {
    return api.get<SiteLinkedSpecialty[]>(`/sites/${id}/specialties`);
  },

  /**
   * Actualizar especialidades de un centro
   */
  updateSpecialties: async (
    id: string,
    data: UpdateSiteSpecialtiesDto
  ): Promise<{ success: boolean; message: string }> => {
    return api.patch<{ success: boolean; message: string }>(
      `/sites/${id}/specialties`,
      data
    );
  },
};

export default sitesApi;
